'use client'

import { useMutation } from '@apollo/client'
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@chain-zen/ui'
import React, { useCallback, useState } from 'react'

import { VERIFY_CODE_FOR_WORKSPACE_MEMBER } from '../../data/mutations/member'
import { useAuth } from '../../hooks/use-auth'

interface IVerifyCodeForWorkspaceMember {
  constraintWorkspaceID: string
  isResending: boolean
  onCloseAction: () => void
  resendCodeAction: () => void
  selectedEmailOrPhone: string | null
}

/**
 * VerifyCodeForWorkspaceMember verifies the verification code for a workspace member
 */
export const VerifyCodeForWorkspaceMember = ({
  constraintWorkspaceID,
  isResending,
  onCloseAction,
  resendCodeAction,
  selectedEmailOrPhone
}: IVerifyCodeForWorkspaceMember) => {
  const [verificationCode, setVerificationCode] = useState<string>('')
  const [verificationErrorMessage, setVerificationErrorMessage] = useState('')
  const { refetchWorkspaces, setSelectedWorkspace } = useAuth()

  const [verifyCodeForWorkspaceMember, { loading }] = useMutation(VERIFY_CODE_FOR_WORKSPACE_MEMBER, {
    onCompleted: async () => {
      // Refetch workspaces and select the newly added workspace
      const { data } = await refetchWorkspaces()
      const newWorkspace = data?.myWorkspaces?.find((ws) => ws.workspace.id === constraintWorkspaceID)
      if (newWorkspace) {
        setSelectedWorkspace(newWorkspace)
      }
      onCloseAction()
    },
    onError: (error) => {
      setVerificationErrorMessage(error.message || 'Something went wrong.')
    }
  })

  const handleVerifyAction = useCallback(() => {
    setVerificationErrorMessage('')
    verifyCodeForWorkspaceMember({
      variables: {
        code: verificationCode,
        workspaceID: constraintWorkspaceID
      }
    })
  }, [verificationCode, constraintWorkspaceID, verifyCodeForWorkspaceMember])

  return (
    <Card className="border-none shadow-none">
      <CardHeader className="px-6 py-4 space-y-1">
        <CardTitle>Enter verification code</CardTitle>
        <p className="text-sm text-muted-foreground">
          Please enter the 6 digit verification code that was sent to{' '}
          <span className="font-medium text-foreground">{selectedEmailOrPhone}</span>.
        </p>
      </CardHeader>
      <CardContent className="px-6 space-y-4">
        <div className="space-y-2">
          <Input
            autoFocus={true}
            placeholder="Please enter your verification code"
            name="otp"
            onChange={(e) => setVerificationCode(e.target.value)}
            className="w-full"
          />
          {verificationErrorMessage.length > 0 && (
            <p className="text-sm text-destructive">{verificationErrorMessage}</p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <Button
            variant="default"
            onClick={handleVerifyAction}
            disabled={verificationCode.length !== 6 || loading}
            className="w-full"
          >
            {loading ? 'Verifying...' : 'Submit'}
          </Button>
          <Button variant="ghost" onClick={resendCodeAction} disabled={isResending} className="w-full">
            {isResending ? 'Sending...' : 'Resend code'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
